import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import formatDate from 'lumen/utils/format-local-date';

export default class HotelParityPosController extends Controller {
  queryParams = [{ maxPersons: { scope: 'controller' } }];

  @service access;
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service ota;
  @service rates;
  @service simpleExport;
  @service usageAnalytics;

  @tracked maxPersons = 0;

  get availableOTAs() {
    // Exception for PA-only hotels and limited parity, they should make PA parities request
    const showParityAnalyticsOTAs =
      this.parityAnalyticsView && this.access.hasParityAnalyticsStaff;

    const otas = showParityAnalyticsOTAs
      ? [...this.hotel.getParityAnalyticsOTAs(this.platform)]
      : [...this.hotel.getParityOTAs(this.platform)];

    return otas.sort((a, b) => a.order - b.order);
  }

  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  get hotelId() {
    return this.hotel.id;
  }

  get isPosAvailable() {
    return this.hotel.OTAsParity?.find((ota) => ota.id === 'trivago')
      ?.isConnected;
  }

  get los() {
    return this.hotel.defaultLosParity;
  }

  @action
  handleExport() {
    if (!this.model?.isLoading) {
      this.usageAnalytics.track('Data Exported', {
        eventTrigger: 'parity-pos-view',
      });

      const allowedOtaIds = this.availableOTAs.map((ota) => ota.id);
      const headers = [
        this.intl.t('general.arrival_date').toString(),
        this.intl.t('parity.pos.pos').toString(),
        this.intl.t('parity.pos.hotel_rate').toString(),
        this.intl.t('general.major_otas').toString(),
        this.intl.t('rates.lowest_rate').toString(),
        this.intl.t('general.other_otas').toString(),
        this.intl.t('rates.lowest_rate').toString(),
      ];
      const title = this.intl.t('parity.pos_view').toString();
      const sheetName = title;

      const content = this.model?.periods.map((period) => {
        const { paritiesPOSDict } = period;
        const date = formatDate(period.day?.date);
        const { pos } = period;

        const majorOtas = Object.keys(paritiesPOSDict).filter(
          (key) =>
            key !== 'others' &&
            allowedOtaIds.indexOf(paritiesPOSDict[key].ota) !== -1,
        );

        const baseRates = [];
        const majorParitiesWithIssues = [];
        const otherParitiesWithIssues = paritiesPOSDict.others.filter(
          (parity) =>
            allowedOtaIds.indexOf(parity.ota) !== -1 &&
            parity.position_to_baserate < 0,
        );

        majorOtas.forEach((ota) => {
          if (paritiesPOSDict[ota].is_baserate) {
            baseRates.push(paritiesPOSDict[ota]);
          }

          if (paritiesPOSDict[ota].position_to_baserate < 0) {
            majorParitiesWithIssues.push(paritiesPOSDict[ota]);
          }
        });

        const hotelRate = baseRates.length
          ? `${this.rates.formatRateObject(baseRates[0], false)} ${baseRates[0].currency}`
          : this.intl.t('general.not_available_short');

        let lowestMajorParityIssue = this.intl.t('general.missing');
        let formattedMajorIssues = this.intl.t('parity.no_issues');
        let lowestOtherParityIssue = this.intl.t('general.missing');
        let formattedOtherIssues = this.intl.t('parity.no_issues');

        if (majorParitiesWithIssues.length) {
          lowestMajorParityIssue = `${this.rates.formatRateObject(
            majorParitiesWithIssues[0],
            false,
          )} ${majorParitiesWithIssues[0].currency}`;
          formattedMajorIssues = majorParitiesWithIssues
            .map((parity) => this.ota.otaIdToPrettyName(parity.channel))
            .join(', ');
        }

        if (otherParitiesWithIssues.length) {
          lowestOtherParityIssue = `${this.rates.formatRateObject(
            otherParitiesWithIssues[0],
            false,
          )} ${otherParitiesWithIssues[0].currency}`;
          formattedOtherIssues = otherParitiesWithIssues
            .map((parity) => this.ota.otaIdToPrettyName(parity.channel))
            .join(', ');
        }

        return [
          date.toString(),
          pos,
          hotelRate.toString(),
          formattedMajorIssues,
          lowestMajorParityIssue.toString(),
          formattedOtherIssues,
          lowestOtherParityIssue.toString(),
        ];
      });

      const postData = JSON.stringify({
        headers,
        content,
        title,
        sheetName,
      });

      this.simpleExport.execute(postData, this.hotelId);
    }
  }
}
